import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useTheme,
} from '@material-ui/core'
import React, {
    CSSProperties,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { Container } from '../../components'
import {
    WORKORDER_TYPE_ACTION_ITEM,
    WORKORDER_TYPE_SERVICE_ORDER,
    WORKORDER_TYPE_SERVICE_REQUEST,
    workorderContext,
} from '../../contexts'
import {
    DateFilters,
    ModelMap,
    Service,
    WorkorderStatusFilters,
    WorkorderType,
    convertMapToList,
    dateFilterOptions,
    filterOptions,
    getDateRange,
} from '../../models'
import { useMultiSelectStyles } from '../../styles'
import { ServiceFilter } from '../../components/ServiceFilter'
import SearchIcon from '@material-ui/icons/Search'
import { useAppDispatch, useUser } from '../../hooks'
import { getUserList } from '../../store'
import { axiosInstance } from '../../helpers'
import {
    SUB_VENDOR_FILTER_ALL,
    SUB_VENDOR_FILTER_UNASSIGNED,
} from './subVendorFilter'

export const EZWorkWorkorderFilters = () => {
    const {
        workorderList,
        workorderFilterState,
        setWorkorderFilterState,
        getWorkorderList,
    } = workorderContext()

    const dispatch = useAppDispatch()
    const { userList, workspaceUser } = useUser()

    const theme = useTheme()

    const classes = useMultiSelectStyles()

    const [dateValue, setDateValue] = useState<DateFilters>(
        dateFilterOptions[0],
    )
    const [statusFilter, setStatusFilter] = useState<WorkorderStatusFilters>(
        'ALL',
    )
    const [subVendorFilterId, setSubVendorFilterId] = useState(
        workspaceUser?.is_sub_vendor
            ? SUB_VENDOR_FILTER_ALL
            : SUB_VENDOR_FILTER_UNASSIGNED,
    )
    const [unassignedCount, setUnassignedCount] = useState<number | null>(null)
    const unassignedCountReqId = useRef(0)

    useEffect(() => {
        void dispatch(getUserList({ params: { my_team: true } }))
    }, [dispatch])

    const fetchUnassignedCount = useCallback(
        (date: DateFilters, status: WorkorderStatusFilters) => {
            if (workspaceUser?.is_sub_vendor) {
                return
            }
            const reqId = ++unassignedCountReqId.current
            const dateRange = getDateRange(date)
            void axiosInstance
                .get<{ count: number }>('workorder/', {
                    params: {
                        upper_bound_date: dateRange.lte_date,
                        lower_bound_date: dateRange.gte_date,
                        wo_status: status,
                        all_jobsites: true,
                        sub_vendor_id: SUB_VENDOR_FILTER_UNASSIGNED,
                        count_only: true,
                    },
                })
                .then((res) => {
                    if (reqId !== unassignedCountReqId.current) {
                        return
                    }
                    const n = res.data?.count
                    if (typeof n === 'number') {
                        setUnassignedCount(n)
                    }
                })
                .catch(() => undefined)
        },
        [workspaceUser?.is_sub_vendor],
    )

    const fetchWorkordersWithFilters = useCallback(
        (overrides?: {
            date?: DateFilters
            status?: WorkorderStatusFilters
            subVendorId?: number
        }) => {
            const d = overrides?.date ?? dateValue
            const s = overrides?.status ?? statusFilter
            const subId = overrides?.subVendorId ?? subVendorFilterId
            const dateRange = getDateRange(d)
            const params: {
                upper_bound_date: string | undefined
                lower_bound_date: string | undefined
                wo_status: WorkorderStatusFilters
                all_jobsites: boolean
                sub_vendor_id?: number
            } = {
                upper_bound_date: dateRange.lte_date,
                lower_bound_date: dateRange.gte_date,
                wo_status: s,
                all_jobsites: true,
            }
            if (
                !workspaceUser?.is_sub_vendor &&
                subId !== SUB_VENDOR_FILTER_ALL
            ) {
                params.sub_vendor_id = subId
            }
            getWorkorderList({ params })
            fetchUnassignedCount(d, s)
        },
        [
            dateValue,
            statusFilter,
            subVendorFilterId,
            getWorkorderList,
            workspaceUser?.is_sub_vendor,
            fetchUnassignedCount,
        ],
    )

    useEffect(() => {
        if (workspaceUser?.is_sub_vendor) {
            return
        }
        if (
            subVendorFilterId === SUB_VENDOR_FILTER_UNASSIGNED &&
            workorderList
        ) {
            setUnassignedCount(workorderList.length)
            return
        }
        fetchUnassignedCount(dateValue, statusFilter)
    }, [workorderList, subVendorFilterId])

    const actionItemLength = workorderList?.filter(
        (workorder) => workorder.type === WorkorderType.ACTION_ITEM,
    ).length
    const serviceOrderLength = workorderList?.filter(
        (workorder) => workorder.type === WorkorderType.SERVICE_ORDER,
    ).length
    const serviceRequestLength = workorderList?.filter(
        (workorder) => workorder.type === WorkorderType.SERVICE_REQUEST,
    ).length

    const workorderServicesMap: ModelMap<Service> = {}
    const workorderApartmentMap: ModelMap<{
        id: number
        name: string
    }> = {}
    const workorderScheduleMap: ModelMap<{
        id: number
        name: string
    }> = {}

    workorderList?.forEach((workorder) => {
        if (
            workorder.service_id &&
            workorderServicesMap[workorder.service_id] === undefined
        ) {
            workorderServicesMap[workorder.service_id] = {
                id: workorder.service_id,
                name: workorder.service_name ?? '',
                color: workorder.service_color ?? theme.palette.noService.main,
                order: workorder.service_id,
                service_category: workorder.service_category,
            }
        }
        if (workorder.apartment_id && workorder.apartment_name) {
            workorderApartmentMap[workorder.apartment_id] = {
                id: workorder.apartment_id,
                name: workorder.apartment_name,
            }
        }
        if (workorder.schedule_id && workorder.schedule_name) {
            workorderScheduleMap[workorder.schedule_id] = {
                id: workorder.schedule_id,
                name: workorder.schedule_name,
            }
        }
    })

    const apartmentList = convertMapToList(workorderApartmentMap)
    const scheduleList = convertMapToList(workorderScheduleMap).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    )

    const panelSurfaceStyle: CSSProperties = {
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius:
            typeof theme.shape.borderRadius === 'number'
                ? theme.shape.borderRadius
                : 8,
        boxSizing: 'border-box',
        boxShadow: theme.shadows[1],
        padding: theme.spacing(2),
        minWidth: 450,
        maxWidth: 450,
        marginBottom: theme.spacing(2),
    }

    return (
        <Container direction="column" style={panelSurfaceStyle}>
            <Typography
                variant="subtitle2"
                color="textSecondary"
                style={{
                    marginBottom: theme.spacing(1),
                    fontWeight: theme.typography.fontWeightBold,
                }}
            >
                Filters
            </Typography>
            <Container flexWrap="wrap">
                <FormControl className={classes.formControl} variant="outlined">
                    <Select
                        labelId="DATE_FILTER_LABEL"
                        value={dateValue}
                        onChange={(
                            event: React.ChangeEvent<{
                                value: unknown
                            }>,
                        ) => {
                            const next = event.target.value as DateFilters
                            setDateValue(next)
                            fetchWorkordersWithFilters({ date: next })
                        }}
                        style={{
                            width: '200px',
                            height: '40px',
                            marginRight: theme.spacing(2),
                        }}
                    >
                        {dateFilterOptions.map((option) => {
                            return (
                                <MenuItem
                                    key={`DATE_FILTER_${option}`}
                                    value={option}
                                >
                                    {option}
                                </MenuItem>
                            )
                        })}
                    </Select>
                </FormControl>
                <FormControl className={classes.formControl} variant="outlined">
                    <InputLabel id="STATUS_FILTER_LABEL">Status</InputLabel>
                    <Select
                        labelId="STATUS_FILTER_LABEL"
                        id="status_filter_label"
                        onChange={(
                            event: React.ChangeEvent<{
                                value: unknown
                            }>,
                        ) => {
                            const next = event.target
                                .value as WorkorderStatusFilters
                            setStatusFilter(next)
                            fetchWorkordersWithFilters({ status: next })
                        }}
                        value={statusFilter}
                        label="Status"
                        style={{
                            width: '200px',
                            height: '40px',
                        }}
                    >
                        {filterOptions.map((option) => {
                            return (
                                <MenuItem
                                    key={`DATE_FILTER_${option.be}`}
                                    value={option.be}
                                >
                                    {option.d}
                                </MenuItem>
                            )
                        })}
                    </Select>
                </FormControl>
            </Container>
            <Container
                style={{
                    margin: theme.spacing(1, 0),
                    alignItems: 'center',
                }}
            >
                {!workspaceUser?.is_sub_vendor && (
                    <FormControl
                        className={classes.formControl}
                        variant="outlined"
                    >
                        <InputLabel id="SUB_VENDOR_FILTER_LABEL">
                            Sub
                        </InputLabel>
                        <Select
                            labelId="SUB_VENDOR_FILTER_LABEL"
                            id="sub_vendor_filter"
                            value={subVendorFilterId}
                            label="Sub"
                            onChange={(
                                event: React.ChangeEvent<{
                                    value: unknown
                                }>,
                            ) => {
                                const next = Number(event.target.value)
                                setSubVendorFilterId(next)
                                fetchWorkordersWithFilters({
                                    subVendorId: next,
                                })
                            }}
                            style={{
                                width: '200px',
                                height: '40px',
                                marginRight: theme.spacing(2),
                            }}
                        >
                            <MenuItem value={SUB_VENDOR_FILTER_UNASSIGNED}>
                                {unassignedCount == null
                                    ? 'Unassigned'
                                    : `Unassigned (${unassignedCount})`}
                            </MenuItem>
                            <MenuItem value={SUB_VENDOR_FILTER_ALL}>
                                All
                            </MenuItem>
                            {[...userList]
                                .sort((a, b) =>
                                    (a.name ?? '').localeCompare(
                                        b.name ?? '',
                                        void 0,
                                        {
                                            sensitivity: 'base',
                                        },
                                    ),
                                )
                                .map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                )}
                <FormControl className={classes.formControl} variant="outlined">
                    <InputLabel id="APARTMENT_FILTER_LABEL">
                        Apartment
                    </InputLabel>
                    <Select
                        labelId="APARTMENT_FILTER_LABEL"
                        value={workorderFilterState.apartmentId}
                        onChange={(
                            event: React.ChangeEvent<{
                                value: unknown
                            }>,
                        ) => {
                            setWorkorderFilterState({
                                ...workorderFilterState,
                                apartmentId: event.target.value as number,
                            })
                        }}
                        style={{
                            width: '200px',
                            height: '40px',
                        }}
                        label="Apartment"
                    >
                        <MenuItem key={`APARTMENT_FILTER_${-1}`} value={-1}>
                            All
                        </MenuItem>
                        {apartmentList.map((apartment) => {
                            return (
                                <MenuItem
                                    key={`APARTMENT_FILTER_${apartment.id}`}
                                    value={apartment.id}
                                >
                                    {apartment.name}
                                </MenuItem>
                            )
                        })}
                    </Select>
                </FormControl>
            </Container>
            <Container
                style={{
                    margin: theme.spacing(1, 0),
                    alignItems: 'center',
                }}
            >
                <FormControl className={classes.formControl} variant="outlined">
                    <InputLabel id="SCHEDULE_FILTER_LABEL">Project</InputLabel>
                    <Select
                        labelId="SCHEDULE_FILTER_LABEL"
                        id="schedule_filter"
                        value={workorderFilterState.schedule}
                        label="Project"
                        onChange={(
                            event: React.ChangeEvent<{
                                value: unknown
                            }>,
                        ) => {
                            setWorkorderFilterState({
                                ...workorderFilterState,
                                schedule: event.target.value as number,
                            })
                        }}
                        style={{
                            width: '200px',
                            height: '40px',
                        }}
                    >
                        <MenuItem key="SCHEDULE_FILTER_-1" value={-1}>
                            All
                        </MenuItem>
                        {scheduleList.map((schedule) => (
                            <MenuItem
                                key={`SCHEDULE_FILTER_${schedule.id}`}
                                value={schedule.id}
                            >
                                {schedule.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Container>
            <Container
                direction="column"
                style={{
                    marginBottom: theme.spacing(1),
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <TextField
                    variant="outlined"
                    label="Search ID or unit"
                    placeholder="Workorder ID or unit name"
                    fullWidth
                    value={workorderFilterState.unitSearch}
                    onChange={(e) => {
                        setWorkorderFilterState({
                            ...workorderFilterState,
                            unitSearch: e.target.value,
                        })
                    }}
                    size="small"
                    InputProps={{
                        endAdornment: <SearchIcon />,
                    }}
                    style={{
                        width: '100%',
                    }}
                />
            </Container>
            <Container>
                <Button
                    style={{
                        border: `1px  solid ${theme.palette.lightGrey.main}`,
                        borderRadius: '20px',
                        textTransform: 'none',
                        backgroundColor:
                            workorderFilterState.workorderTypeFilter ===
                            WorkorderType.SERVICE_ORDER
                                ? theme.palette.primary.dark
                                : '',
                        color:
                            workorderFilterState.workorderTypeFilter ===
                            WorkorderType.SERVICE_ORDER
                                ? 'white'
                                : '',
                    }}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setWorkorderFilterState({
                            ...workorderFilterState,
                            workorderType: WORKORDER_TYPE_SERVICE_ORDER,
                        })
                    }}
                >
                    {`Service Orders (${serviceOrderLength ?? 0})`}
                </Button>
                <Button
                    style={{
                        margin: theme.spacing(0, 2, 0, 2),
                        border: `1px  solid ${theme.palette.lightGrey.main}`,
                        borderRadius: '20px',
                        textTransform: 'none',
                        backgroundColor:
                            workorderFilterState.workorderType ===
                            WORKORDER_TYPE_ACTION_ITEM
                                ? theme.palette.primary.dark
                                : '',
                        color:
                            workorderFilterState.workorderType ===
                            WORKORDER_TYPE_ACTION_ITEM
                                ? 'white'
                                : '',
                    }}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setWorkorderFilterState({
                            ...workorderFilterState,
                            workorderType: WORKORDER_TYPE_ACTION_ITEM,
                        })
                    }}
                >
                    {`Action Items (${actionItemLength ?? 0})`}
                </Button>
                <Button
                    style={{
                        border: `1px  solid ${theme.palette.lightGrey.main}`,
                        borderRadius: '20px',
                        textTransform: 'none',
                        backgroundColor:
                            workorderFilterState.workorderType ===
                            WORKORDER_TYPE_SERVICE_REQUEST
                                ? theme.palette.primary.dark
                                : '',
                        color:
                            workorderFilterState.workorderType ===
                            WORKORDER_TYPE_SERVICE_REQUEST
                                ? 'white'
                                : '',
                    }}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setWorkorderFilterState({
                            ...workorderFilterState,
                            workorderType: WORKORDER_TYPE_SERVICE_REQUEST,
                        })
                    }}
                >
                    {`Service Requests (${serviceRequestLength ?? 0})`}
                </Button>
            </Container>
            <Container style={{ marginTop: theme.spacing(1) }}>
                <ServiceFilter
                    serviceList={convertMapToList(workorderServicesMap)}
                    selectedServiceIds={workorderFilterState.serviceIds}
                    onSelectService={(serviceId) => {
                        if (
                            workorderFilterState.serviceIds.find(
                                (servId) => servId === serviceId,
                            )
                        ) {
                            setWorkorderFilterState({
                                ...workorderFilterState,
                                serviceIds: workorderFilterState.serviceIds.filter(
                                    (servId) => servId !== serviceId,
                                ),
                            })
                        } else {
                            const newServiceIdList = [
                                ...workorderFilterState.serviceIds,
                                serviceId,
                            ]
                            setWorkorderFilterState({
                                ...workorderFilterState,
                                serviceIds: newServiceIdList,
                            })
                        }
                    }}
                />
            </Container>
        </Container>
    )
}
